import { useState } from "react";
import { APPS_SCRIPT_URL as SCRIPT_URL } from "../lib/appsScript";

const debug = window.location.search.includes("debug=1");

const initialForm = {
    name: "",
    feedback: "",
    desiredState: "",
    suggestions: "",
};

export default function Feedback() {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    const filledClass = (value) =>
        value && String(value).trim() !== "" ? " reg-input--filled" : "";

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMsg("");
        setErrorMsg("");

        try {
            const body = new URLSearchParams();

            body.set("action", "feedback");

            Object.entries(form).forEach(([key, value]) => {
                body.set(key, value ?? "");
            });

            const res = await fetch(SCRIPT_URL, {
                method: "POST",
                body,
            });

            if (res.type === "opaque") {
                throw new Error(
                    "Apps Script hat keine lesbare Antwort geliefert. Erfolg kann nicht bestätigt werden."
                );
            }

            const text = await res.text();
            console.log("Apps Script feedback response raw:", text);

            let json;
            try {
                json = JSON.parse(text);
            } catch {
                throw new Error("Konnte Antwort vom Script nicht als JSON lesen: " + text);
            }

            if (!res.ok || !json.ok) {
                throw new Error(json.error || "Unbekannter Fehler vom Apps Script.");
            }

            setSuccessMsg(
                `Vielen Dank für dein Feedback.`
            );
            setForm(initialForm);
        } catch (err) {
            console.error("Fehler beim Absenden des Feedbacks:", err);
            setErrorMsg(
                err?.message ||
                "Dein Feedback konnte gerade nicht gespeichert werden. Bitte probiere es später erneut oder kontaktiere die Orga direkt."
            );
        } finally {
            setLoading(false);
        }
    };

    const labelStyle = {
        alignSelf: "start",
        justifySelf: "flex-end",
        marginRight: "8px",
        paddingTop: "10px",
        fontWeight: 600,
        fontFamily:
            'var(--ff-Kallisto), "Rajdhani", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
        fontSize: "0.95rem",
    };

    const fullRow = { gridColumn: "1 / -1" };

    const textareaStyle = {
        minHeight: "120px",
        resize: "vertical",
        lineHeight: "1.45",
        paddingTop: "12px",
        fontFamily: 'var(--ff-Everett), "Rajdhani", system-ui, sans-serif',
    };

    return (
        <div className="card card--accent">
            <h1>Feedback</h1>

            <p
                style={{
                    marginBottom: "18px",
                    fontFamily: "var(--ff-Everett), sans-serif",
                }}
            >
                Hier kannst du uns Feedback zur Liga, Organisation, Kommunikation oder
                allgemein zu RaceVision geben. Das darf Kritik sein, positives Feedback
                oder auch ein konkreter Vorschlag, was wir besser machen können.
            </p>

            {debug && (
                <pre
                    style={{
                        fontSize: "12px",
                        padding: "10px",
                        background: "rgba(0,0,0,0.4)",
                        border: "1px solid #444",
                        marginBottom: "14px",
                        color: "#e4fe3c",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    DEBUG MODE AKTIV
                    <br />
                    Script URL: {SCRIPT_URL}
                    {"\n"}Aktueller Form-State: {JSON.stringify(form, null, 2)}
                </pre>
            )}

            {successMsg && (
                <div
                    style={{
                        marginBottom: "12px",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid rgba(105,251,144,0.6)",
                        background: "rgba(105,251,144,0.08)",
                        color: "#69fb90",
                        fontSize: "0.96rem",
                    }}
                >
                    {successMsg}
                </div>
            )}

            {errorMsg && (
                <div
                    style={{
                        marginBottom: "12px",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,44,70,0.6)",
                        background: "rgba(255,44,70,0.08)",
                        color: "#ff8c8c",
                        fontSize: "0.96rem",
                    }}
                >
                    {errorMsg}
                </div>
            )}

            <form
                onSubmit={handleSubmit}
                style={{
                    marginTop: "16px",
                    display: "grid",
                    gridTemplateColumns: "max(190px, 30%) minmax(0, 1fr)",
                    columnGap: "18px",
                    rowGap: "12px",
                    maxWidth: "880px",
                    width: "100%",
                }}
                className="reg-grid"
            >
                <label htmlFor="name" style={labelStyle}>
                    Name
                </label>
                <input
                    id="name"
                    className={`reg-input${filledClass(form.name)}`}
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Optional"
                />

                <label htmlFor="feedback" style={labelStyle}>
                    Was für ein Feedback möchtest du uns geben?
                </label>
                <textarea
                    id="feedback"
                    className={`reg-input${filledClass(form.feedback)}`}
                    name="feedback"
                    value={form.feedback}
                    onChange={handleChange}
                    placeholder="Dies kann natürlich Kritik oder auch positives Feedback sein."
                    style={textareaStyle}
                    required
                />

                <label htmlFor="desiredState" style={labelStyle}>
                    Was wünschst du dir für einen Zustand?
                </label>
                <textarea
                    id="desiredState"
                    className={`reg-input${filledClass(form.desiredState)}`}
                    name="desiredState"
                    value={form.desiredState}
                    onChange={handleChange}
                    placeholder="Mit welchem Zustand wärst du zufrieden, sodass du dich wieder wohl fühlst?"
                    style={textareaStyle}
                />

                <label htmlFor="suggestions" style={labelStyle}>
                    Was für konkrete Vorschläge hast du?
                </label>
                <textarea
                    id="suggestions"
                    className={`reg-input${filledClass(form.suggestions)}`}
                    name="suggestions"
                    value={form.suggestions}
                    onChange={handleChange}
                    placeholder="Welche konkreten Ideen, Änderungen oder Maßnahmen würdest du dir wünschen?"
                    style={textareaStyle}
                />

                <div
                    style={{
                        ...fullRow,
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "6px",
                    }}
                >
                    <button
                        className="button button--primary"
                        type="submit"
                        disabled={loading}
                        style={{
                            minWidth: "180px",
                            padding: "10px 18px",
                            fontFamily:
                                'var(--ff-Kallisto), "Rajdhani", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                            fontWeight: 700,
                            letterSpacing: ".04em",
                            textTransform: "uppercase",
                            fontSize: "0.9rem",
                        }}
                    >
                        {loading ? "Sende Feedback…" : "Feedback senden"}
                    </button>
                </div>
            </form>
        </div>
    );
}